import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Activity, CheckCircle2, AlertTriangle, XCircle, MinusCircle,
  RefreshCw, Zap, Clock,
} from "lucide-react";
import { api } from "@/lib/api";

/**
 * /admin/apis · Health-check ao vivo das integrações críticas.
 * Pings paralelos com timeout 3s cada · atualiza a cada 60s.
 */

const STATUS_STYLE = {
  operational:    { color: "#10B981", bg: "#DCFCE7", ring: "#86EFAC", label: "OPERACIONAL", Icon: CheckCircle2 },
  degraded:       { color: "#F59E0B", bg: "#FEF3C7", ring: "#FCD34D", label: "INSTÁVEL",    Icon: AlertTriangle },
  down:           { color: "#DC2626", bg: "#FEE2E2", ring: "#FCA5A5", label: "INDISPONÍVEL", Icon: XCircle },
  not_configured: { color: "#64748B", bg: "#F1F5F9", ring: "#CBD5E1", label: "NÃO CONFIG.",  Icon: MinusCircle },
};

const CAT_ORDER = ["Payments", "AI", "Legal", "Integrations", "Communications", "Content", "Database"];

export default function AdminApisHealth() {
  const [data, setData] = useState(null);
  const [history, setHistory] = useState({}); // { [name]: {uptime_pct, avg_latency_ms} }
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [historyWindow, setHistoryWindow] = useState(7);

  const load = async () => {
    setRefreshing(true);
    try {
      const [r, h] = await Promise.all([
        api.get("/admin/apis/health"),
        api.get(`/admin/apis/history?window_days=${historyWindow}`).catch(() => ({ data: { items: [] } })),
      ]);
      setData(r.data);
      const map = {};
      for (const it of (h.data?.items || [])) map[it.name] = it;
      setHistory(map);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao consultar health-check");
    } finally { setLoading(false); setRefreshing(false); }
  };
  useEffect(() => { load(); }, [historyWindow]);
  useEffect(() => {
    if (!autoRefresh) return;
    const iv = setInterval(load, 60000);
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, [autoRefresh, historyWindow]);

  const summary = data?.summary || {};
  const checks = data?.checks || [];
  const grouped = checks.reduce((acc, c) => {
    (acc[c.category] = acc[c.category] || []).push(c);
    return acc;
  }, {});
  const cats = Object.keys(grouped).sort((a, b) => {
    const ai = CAT_ORDER.indexOf(a); const bi = CAT_ORDER.indexOf(b);
    return (ai < 0 ? 99 : ai) - (bi < 0 ? 99 : bi);
  });

  return (
    <div className="p-3 sm:p-6 lg:p-8" data-testid="admin-apis-health">
      {/* HEADER */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="flex items-center gap-2 text-[#d4a24c]">
            <Activity className="w-5 h-5"/>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold">Health-check</div>
          </div>
          <h1 className="font-serif text-2xl text-white mt-1">Central de APIs & Integrações</h1>
          <p className="text-sm text-slate-400 mt-1">
            Ping ao vivo (timeout 3s) · atualiza a cada 60s · consulte antes de lançar novos módulos.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={historyWindow} onChange={(e)=>setHistoryWindow(parseInt(e.target.value))}
            data-testid="apis-window"
            className="text-xs bg-[#0f1220] border border-white/10 rounded px-2 py-1.5 text-slate-300">
            <option value={1}>últimas 24h</option>
            <option value={7}>últimos 7 dias</option>
            <option value={30}>últimos 30 dias</option>
          </select>
          <label className="text-xs text-slate-400 inline-flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={autoRefresh} onChange={(e)=>setAutoRefresh(e.target.checked)}
              data-testid="apis-auto-refresh"/>
            auto (60s)
          </label>
          <button onClick={load} disabled={refreshing}
            data-testid="apis-refresh-btn"
            className="text-xs px-3 py-1.5 rounded bg-[#d4a24c] hover:bg-[#e0b45f] text-[#0b0f1a] font-semibold inline-flex items-center gap-1.5 disabled:opacity-60">
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`}/> Atualizar
          </button>
        </div>
      </div>

      {/* SUMMARY */}
      {!loading && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="apis-summary">
          <SummaryChip label="Total"          value={summary.total || 0} color="#94A3B8"/>
          <SummaryChip label="Operacional"    value={summary.operational || 0} color={STATUS_STYLE.operational.color}/>
          <SummaryChip label="Instável"       value={summary.degraded || 0}    color={STATUS_STYLE.degraded.color}/>
          <SummaryChip label="Indisponível"   value={summary.down || 0}        color={STATUS_STYLE.down.color}/>
          <SummaryChip label="Não configurada" value={summary.not_configured || 0} color={STATUS_STYLE.not_configured.color}/>
        </div>
      )}

      {loading ? (
        <div className="text-slate-400 text-sm py-10 text-center" data-testid="apis-loading">
          Testando integrações…
        </div>
      ) : (
        <div className="space-y-6">
          {cats.map(cat => (
            <section key={cat} data-testid={`apis-cat-${cat.toLowerCase()}`}>
              <div className="text-[10.5px] uppercase tracking-[0.24em] text-slate-500 font-bold mb-2">
                {cat}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {grouped[cat].map(c => <ApiCard key={c.name} c={c} history={history[c.name]} window={historyWindow}/>)}
              </div>
            </section>
          ))}
          {data?.tested_at && (
            <div className="text-[10px] text-slate-500 text-right mt-4" data-testid="apis-tested-at">
              Testado em: {new Date(data.tested_at).toLocaleString("pt-BR")}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SummaryChip({ label, value, color }) {
  return (
    <div className="rounded-xl bg-[#0f1220] border border-white/5 p-3"
         style={{ borderLeftWidth: 3, borderLeftColor: color }}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className="font-serif text-2xl text-white mt-0.5" style={{ color }}>{value}</div>
    </div>
  );
}

function ApiCard({ c, history, window }) {
  const s = STATUS_STYLE[c.status] || STATUS_STYLE.down;
  const Icon = s.Icon;
  const uptime = history?.uptime_pct;
  const uptimeColor = uptime == null ? "#64748B"
                    : uptime >= 99 ? "#10B981"
                    : uptime >= 95 ? "#F59E0B" : "#DC2626";
  return (
    <div className="rounded-xl bg-[#0f1220] border border-white/5 p-4 hover:border-white/10 transition"
         data-testid={`api-card-${c.name.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}>
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
             style={{ background: s.bg, color: s.color, border: `1px solid ${s.ring}` }}>
          <Icon className="w-4 h-4"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div className="text-white text-sm font-medium truncate">{c.name}</div>
          </div>
          <div className="text-[10px] uppercase tracking-widest font-bold mt-0.5"
               style={{ color: s.color }}>{s.label}</div>
          <div className="text-[11px] text-slate-400 mt-1.5 leading-snug">{c.message}</div>
          <div className="flex items-center gap-3 mt-2 text-[10px] text-slate-500">
            {c.latency_ms > 0 && (
              <span className="inline-flex items-center gap-1">
                <Zap className="w-3 h-3"/> {c.latency_ms}ms
              </span>
            )}
            {c.endpoint && (
              <span className="font-mono truncate max-w-[180px]" title={c.endpoint}>
                {c.endpoint}
              </span>
            )}
          </div>
          {uptime != null && (
            <div className="mt-2 pt-2 border-t border-white/5" data-testid={`api-uptime-${c.name.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}>
              <div className="flex items-center justify-between mb-0.5">
                <span className="text-[9.5px] uppercase tracking-widest text-slate-500 font-semibold">
                  Uptime {window}d
                </span>
                <span className="text-[11px] font-bold" style={{ color: uptimeColor }}>
                  {uptime}%
                </span>
              </div>
              <div className="h-1 rounded bg-[#1a1f2e] overflow-hidden">
                <div className="h-full transition-all" style={{ width: `${uptime}%`, background: uptimeColor }}/>
              </div>
              <div className="flex items-center justify-between mt-1 text-[9.5px] text-slate-500">
                <span>{history.total} pings · avg {history.avg_latency_ms}ms</span>
                <span>down: {history.down}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
